import { CreateFileParams } from '../../../params/redux/file';
import { createFileThunk } from '../../../redux/thunks/file';
import CustomInput from '../../reusable/input';
import CustomModal from '../../modals';
import FolderApi from '../../../apis/folder';
import { gray } from '../../../theme/themePrimitives';
import { replaceToken } from '../../../redux/slices';
import { useParams } from 'react-router';
import { AppDispatch, RootState } from '../../../redux/store';
import { Box, Button, FormControlLabel, MenuItem, Radio, RadioGroup, Select, Typography } from '@mui/material';
import { useDispatch, useSelector } from 'react-redux';
import { useRef, useState } from 'react';


const UploadFileModal = ({ open, onClose }: { open: boolean; onClose: () => void }) => {
    const dispatch = useDispatch<AppDispatch>();
    const { id } = useParams<{ id: string }>();
    const [selectedOption, setSelectedOption] = useState('existingFolder');
    const [newFolderName, setNewFolderName] = useState('');
    const [isLoading, setIsLoading] = useState<boolean>(false);
    const [selectedFolder, setSelectedFolder] = useState('0');
    const fileInputRef = useRef<HTMLInputElement | null>(null);
    const { loaders, folders } = useSelector((state: RootState) => state.folder);
    const { token } = useSelector((state: RootState) => state.app);
    const disabled = isLoading || loaders.isCreating;

    const handleFileSelect = () => {
        if (fileInputRef.current) {
            fileInputRef.current.click();
        }
    };

    const handleApiCall = async (data: { mode: string; folderName?: string; fileBase64: string; fileName: string }) => {
        try {
            // const fileFormat = fetchImageFormatFromBase64(data.fileBase64 as string);
            const fileData: CreateFileParams = {
                client_id: id ? parseInt(id as string, 10) : 0,
                file: data.fileBase64,
                filename: data.fileName,
                folder_id: '0'
            };
            if (data.mode === 'newFolder') {
                const folderResponse = await FolderApi.createFolder(token, data.folderName as string, id ? parseInt(id as string, 10) : 0);
                if (folderResponse.success === 1) {
                    dispatch(replaceToken(folderResponse.token));
                    fileData.folder_id = folderResponse.folder?.id.toString() || '0';
                }
            } else if (data.mode === 'existingFolder') {
                fileData.folder_id = data.folderName?.toString() || '0';
            }
            await dispatch(createFileThunk(fileData)).unwrap();
        } catch (error) {
            // Catch errors
        }
    };

    const fileToBase64 = (file: File): Promise<string> => {
        return new Promise((resolve, reject) => {
            const reader = new FileReader();
            reader.readAsDataURL(file);
            reader.onload = () => {
                if (reader.result) {
                    resolve(reader.result as string);
                } else {
                    reject(new Error('File reading failed.'));
                }
            };
            reader.onerror = (error) => reject(error);
        });
    };

    const handleFileChange = async (event: React.ChangeEvent<HTMLInputElement>) => {
        const file = event.target.files?.[0];
        if (!file) return;

        setIsLoading(true);
        const fileBase64: string = await fileToBase64(file);

        const data = selectedOption === 'newFolder'
            ? { mode: 'newFolder', folderName: newFolderName, fileBase64, fileName: file.name }
            : selectedOption === 'existingFolder'
                ? { mode: 'existingFolder', folderName: selectedFolder, fileBase64, fileName: file.name }
                : { mode: 'individualFile', fileBase64, fileName: file.name };
        await handleApiCall(data);
        setIsLoading(false);
        // Close the modal after successful upload
        onClose();
    };

    const handleConfirm = () => {
        handleFileSelect();
    };

    return <CustomModal open={open} handleClose={disabled ? () => null : onClose} title="Locate your file">
        <Box>
            <Typography variant="body1" mb={2}>
                Choose your desired path
            </Typography>
            <RadioGroup
                value={selectedOption}
                onChange={(e) => setSelectedOption(e.target.value)}
                sx={{
                    width: '100%'
                }}
            >
                {/* Add to New Folder */}
                <Box border={`1px solid ${gray[200]}`} p={1} borderRadius="10px" width="100%">
                    <FormControlLabel
                        value="newFolder"
                        control={<Radio />}
                        sx={{
                            'width': '100%',
                            '& .MuiFormControlLabel-label': {
                                width: '100%'
                            }
                        }}
                        disabled={disabled}
                        label={
                            <Box width="100%">
                                <Typography mb={1}>Add to new folder</Typography>
                                {selectedOption === 'newFolder' && (
                                    <CustomInput
                                        value={newFolderName}
                                        onChange={(e) => setNewFolderName(e.target.value)}
                                        placeholder="Folder Name"
                                        fullWidth
                                        disabled={disabled}
                                    />
                                )}
                            </Box>
                        }
                    />
                </Box>
                {/* Add to Existing Folder */}
                <Box border={`1px solid ${gray[200]}`} p={1} borderRadius="10px" my={2}>
                    <FormControlLabel
                        value="existingFolder"
                        control={<Radio />}
                        sx={{
                            'width': '100%',
                            '& .MuiFormControlLabel-label': {
                                width: '100%'
                            }
                        }}
                        disabled={disabled}
                        label={
                            <Box>
                                <Typography mb={1}>Add to existing folder</Typography>
                                {selectedOption === 'existingFolder' && (
                                    <Select
                                        value={selectedFolder}
                                        onChange={(e) => setSelectedFolder(e.target.value)}
                                        fullWidth
                                    >
                                        {folders.map((folder) => (
                                            <MenuItem key={folder.id} value={folder.id}>
                                                {folder.name}
                                            </MenuItem>
                                        ))}
                                    </Select>
                                )}
                            </Box>
                        }
                    />
                </Box>
                {/* Upload as Individual File */}
                <Box border={`1px solid ${gray[200]}`} p={1} borderRadius="10px">
                    <FormControlLabel
                        value="individualFile"
                        control={<Radio />}
                        label="Upload as individual file"
                        disabled={disabled}
                    />
                </Box>
            </RadioGroup>
            <input
                type="file"
                ref={fileInputRef}
                onChange={handleFileChange}
                style={{ display: 'none' }}
                disabled={disabled}
                accept="*/*"
            />
            {/* Action Buttons */}
            <Box display="flex" gap={2} mt={3}>
                <Button variant="outlined" color="secondary" onClick={onClose} disabled={disabled} fullWidth>
                    Cancel
                </Button>
                <Button
                    variant="contained"
                    color="primary"
                    onClick={handleConfirm}
                    disabled={disabled}
                    fullWidth
                >
                    {disabled ? 'Uploading...' : 'Confirm'}
                </Button>
            </Box>
        </Box>
    </CustomModal>;
};

export default UploadFileModal;